import type { ReactNode } from 'react';
import { useState } from 'react';
import type { FieldMetadata, ObjectMetadata, SavedViewMode } from '../../data/client';
import {
  BottomSheet,
  Button,
  type IconName,
  SegmentedControl,
  SheetNavigator,
  type SheetPage,
} from '../../ui';
import { EMPTY_RELATION_CONTEXT, type RelationContext } from '../filters/filterModel';
import { GroupControls } from './table/GroupControls';
import {
  ColumnsSubPage,
  FilterSubPage,
  SHEET_LABEL,
  SortSubPage,
  type TableControlProps,
  type TableSheetPageId,
} from './table/TableSheetControls';
import { type RecordSegment, TableToolbar } from './table/TableToolbar';
import { useTableControls } from './table/useTableControls';
import type { ViewState } from './views/viewState';

/**
 * EVERY LIST CONTROL A PHONE HAS, behind one button (2026-07-29, after Arif opened an
 * object list on his own phone).
 *
 * What he saw: SIX rows of chrome above the first record - title and description, the
 * mode switch with Duplicates, New, the view switcher, the segment with Filter and Sort,
 * and Columns on its own line. Responsive, and not designed: two thirds of a 375px screen
 * spent on buttons for a screen whose job is to show records.
 *
 * So the phone gets ONE control row - the view switcher and this button - and everything
 * else moves in here. A sheet rather than a menu because these are SETTINGS, plural and
 * mixed (two segmented controls, three pages, one navigation): a dropdown of controls
 * that each open another dropdown is the shape this replaced.
 *
 * AND SINCE 2026-07-30 IT DRILLS IN (Arif, same phone, next round). Filter, Sort and
 * Columns were still the DESKTOP menus with full-width triggers, so tapping one floated a
 * popover over the sheet - a menu over a modal layer, which iOS never does and which our
 * own DESIGN.md rules out ("avoid layering glass on glass"). They are drill-in rows now:
 * each carries its current value and pushes a sub-page inside this same sheet, with a back
 * control, exactly as a navigation stack inside a sheet behaves on iOS.
 *
 * IT DOES NOT CLOSE ITSELF on a change, at either level. Somebody adjusting a list usually
 * adjusts two things, and every control's effect is visible on the list behind the sheet.
 * The one exception is the duplicates row, which navigates away - so it closes first
 * rather than leaving a modal open across a route change.
 *
 * MODE LIVES HERE TOO, which is why this component sits on the page and not in the table:
 * in board mode there is no table to hang it on. The table's own controls are rendered by
 * `TableToolbar` in its sheet layout - the same component the desktop row uses, so the two
 * shapes cannot drift - and they are absent in board mode for the same reason they are
 * absent from the desktop board: a board has no columns to hide and no archive to browse.
 */
/**
 * TABLE OR BOARD, defined once and rendered twice: by the desktop page header and by
 * this sheet. Two lists would be two orders and two label spellings the day somebody
 * adds a third mode.
 */
export const LIST_MODES: readonly { value: SavedViewMode; label: string; icon: IconName }[] = [
  { value: 'table', label: 'Table', icon: 'table' },
  { value: 'board', label: 'Board', icon: 'board' },
];

/** The sub-page headings, in the words the rows that push them use. */
const PAGE_TITLES: Readonly<Record<TableSheetPageId, string>> = {
  filter: 'Filter',
  sort: 'Sort',
  columns: 'Columns',
  group: 'Group',
};

export interface ListAdjustSheetProps {
  readonly object: ObjectMetadata;
  readonly fields: readonly FieldMetadata[];
  /**
   * D27's anchor set plus the fields behind it (D21), read ONCE by the page and handed
   * down. Two placements of one editor must not read it twice or disagree about it, and
   * the page needs it anyway - `reconcileView` validates a stored anchor against it.
   */
  readonly relations?: RelationContext | undefined;

  /** The page's view state (D20) and the one writer for it. */
  readonly state: ViewState;
  readonly onStateChange: (next: (current: ViewState) => ViewState) => void;
  readonly mode: SavedViewMode;
  readonly onModeChange: (next: SavedViewMode) => void;
  readonly segment: RecordSegment;
  readonly onSegmentChange: (next: RecordSegment) => void;
  /** The object-level possible-duplicates list (identity-resolution.md 2.2). */
  readonly onDuplicates: () => void;
  /**
   * The export control (csv-import-export.md D32), passed in rather than built here: it
   * needs the MATERIALISED filter, which only the page holds - the sheet edits the stored
   * form, where a relative-date token is still a token.
   */
  readonly exportAction?: ReactNode;
}

export function ListAdjustSheet({
  object,
  relations = EMPTY_RELATION_CONTEXT,
  fields,
  state,
  onStateChange,
  mode,
  onModeChange,
  segment,
  onSegmentChange,
  onDuplicates,
  exportAction,
}: ListAdjustSheetProps) {
  const [open, setOpen] = useState(false);
  /**
   * WHICH SUB-PAGE IS SHOWING, owned here rather than by the navigator, because the sheet
   * that owns `open` is the thing that has to reset it: a sheet that reopened on the
   * Columns page somebody left it on would have lost its place.
   */
  const [requestedPage, setPage] = useState<TableSheetPageId | null>(null);
  /**
   * ALL THREE SUB-PAGES ARE TABLE CONTROLS, so board mode has none - and the page is
   * DERIVED rather than reset in an effect. A stored id with no page behind it would put
   * the navigator in a state where the root is showing and the back control is not:
   * unreachable through this UI today (the mode switch lives on the root, which a sub-page
   * covers), and one new entry point away from being reachable tomorrow.
   */
  const page = mode === 'table' ? requestedPage : null;
  /**
   * The table's controls read the same derivations the desktop toolbar does, from the same
   * hook - so "which columns are shown" is answered once for every shape.
   */
  const { columns, shownColumns, sort, setSort, toggleColumn, setFilter, grouping } =
    useTableControls({
      object,
      fields,
      state,
      onStateChange,
    });
  const filter = state.filter;

  const controls: TableControlProps = {
    objectId: object.id,
    relations,
    columns,
    shownFieldIds: shownColumns.map((column) => column.field.id),
    onToggleColumn: toggleColumn,
    sort,
    onSortChange: setSort,
    filter,
    onFilterChange: setFilter,
    grouping,
  };

  /**
   * WHAT THE DOT MEANS: this list is not showing everything it could.
   *
   * The archived half is deliberately conditional on the mode. The segment is a TABLE
   * control - the board ignores it - so a dot that stayed lit on a board because the
   * table behind it was left in the archive would be pointing at nothing the user can
   * see. A filter narrows both, so it counts in both.
   */
  const filtered = filter !== null;
  const archived = mode === 'table' && segment === 'archived';
  const note = filtered
    ? archived
      ? 'A filter is active and archived records are shown.'
      : 'A filter is active.'
    : archived
      ? 'Archived records are shown.'
      : null;

  /**
   * Board mode has no sub-pages, for the same reason it has no columns section: they are
   * TABLE controls. Handing the navigator an empty list is also what makes a mode switch
   * from inside a sub-page impossible to get stuck in - there would be no page to show.
   */
  const pages: readonly SheetPage[] =
    mode === 'table'
      ? [
          {
            id: 'filter',
            title: PAGE_TITLES.filter,
            content: <FilterSubPage {...controls} onDone={() => setPage(null)} />,
          },
          { id: 'sort', title: PAGE_TITLES.sort, content: <SortSubPage {...controls} /> },
          { id: 'columns', title: PAGE_TITLES.columns, content: <ColumnsSubPage {...controls} /> },
          {
            id: 'group',
            title: PAGE_TITLES.group,
            /**
             * The same component the desktop popover renders, laid out for a full-width
             * sheet - which is the arrangement the filter builder already has, and for the
             * same reason: this is a second PLACEMENT of one set of controls, never a
             * second set of semantics.
             */
            content: (
              <GroupControls columns={columns} shownColumns={shownColumns} grouping={grouping} />
            ),
          },
        ]
      : [];

  return (
    <>
      <span className="relative inline-flex shrink-0">
        <Button
          /* THE PHONE'S VIEW OPTIONS STAND ON THE CONTENT GROUND (wave 3, L-B2), beside the
             view chip and in the same row as it, so they take the same kit master: a
             content-area push button rather than the glass capsule of the window frame. It
             was `secondary`, which is `.glass-control`, and the two controls in this one row
             then read as two different materials. */
          variant="push"
          icon="filter"
          // What this button opens, for a screen reader that cannot see it happen. Our
          // Dialog is controlled rather than wrapped around a Radix Trigger - the trigger
          // sits in a toolbar row and the sheet is a portal - so the one attribute the
          // Trigger would have contributed is spelled out here.
          aria-haspopup="dialog"
          /**
           * THE STATE IS A SENTENCE, and the dot beside it is the same thing as a
           * picture. ViewSwitcher argues this for "Unsaved changes": a coloured dot is a
           * convention people have to learn, so what is read out loud says it in words.
           *
           * A LABEL rather than a second `sr-only` span inside the button: the name
           * algorithm joins child nodes with a space, so "Adjust" plus ". A filter is
           * active." became "Adjust . A filter is active." - and the label still OPENS
           * with the visible word, which is what voice control matches on (WCAG 2.5.3).
           * Absent entirely when nothing is active, so the name is just "Adjust".
           */
          {...(note !== null ? { 'aria-label': `Adjust. ${note}` } : {})}
          onClick={() => setOpen(true)}
        >
          Adjust
        </Button>
        {note !== null && (
          <span
            aria-hidden="true"
            className="pointer-events-none absolute -top-0.5 -right-0.5 size-2.5 rounded-full bg-accent ring-2 ring-canvas"
          />
        )}
      </span>

      <BottomSheet
        open={open}
        onOpenChange={(next) => {
          setOpen(next);
          // Closing RESETS the stack. Reopening lands where the sheet starts, which is
          // what "Adjust" promises; anything else is a modal that remembers a detour.
          if (!next) setPage(null);
        }}
        title="Adjust"
        /**
         * The sentence explains the ROOT. On a sub-page it would be a paragraph about the
         * screen the user just left, sitting under a heading that says "Columns".
         */
        {...(page === null
          ? {
              description:
                'Choose what this list shows, how it is ordered and which values appear on each card.',
            }
          : {})}
      >
        <SheetNavigator
          pages={pages}
          pageId={page}
          onPageChange={(next) => setPage(next as TableSheetPageId | null)}
        >
          <div className="space-y-5">
            <section className="flex flex-col gap-2">
              <h3 className={SHEET_LABEL}>View</h3>
              <SegmentedControl
                className="w-full"
                value={mode}
                onValueChange={onModeChange}
                ariaLabel="View"
                segments={LIST_MODES.map((entry) => ({
                  value: entry.value,
                  label: entry.label,
                  icon: entry.icon,
                }))}
              />
            </section>

            {mode === 'table' && (
              <TableToolbar
                {...controls}
                segment={segment}
                onSegmentChange={onSegmentChange}
                onDrillIn={setPage}
              />
            )}

            {/*
              THE TWO ROWS THAT LEAVE THIS SCREEN END THE ROOT'S CONTENT, and are no longer
              this sheet's `footer`. C3-1 moved a phone sheet's footer into the bar at the
              TOP, where a caller's row is read as the sheet's Cancel and its confirming
              action - so handing this block over put "Export" and "Review possible
              duplicates" up there and left the sheet with no way out at all, which is the
              60-second timeout the second gate hit. With no footer, Dialog draws the
              top-bar Cancel it already drew on every sub-page of this sheet, and its body
              carries the home-indicator inset itself, so the last tappable row still
              clears the indicator.

              THEY BELONG TO THE ROOT: a sub-page is one task, and a row that navigates out
              of the product from inside it is not part of that task. The export sits above
              the duplicates row because exporting is about the list somebody has just
              finished narrowing, which is exactly what they were doing in this sheet.
            */}
            <div className="space-y-2">
              {exportAction}
              <Button
                variant="secondary"
                icon="users"
                block
                onClick={() => {
                  setOpen(false);
                  setPage(null);
                  onDuplicates();
                }}
              >
                Review duplicates
              </Button>
            </div>
          </div>
        </SheetNavigator>
      </BottomSheet>
    </>
  );
}
